/**
 * Avatar image cache helper.
 *
 * Tracks avatar / group-image URLs that the browser has already fetched, so
 * the chat list (e.g. LatestMessages) can render them with their final
 * opacity instead of running a fade-in transition every time the component
 * remounts (which is what makes profile images appear "slow" when switching
 * filter tabs such as Direct Messages).
 *
 * Usage:
 *   - Call `preloadAvatar(url)` as soon as a list of chats arrives. This
 *     pulls every avatar into the browser HTTP cache in the background and,
 *     on success, marks the URL as `loaded` here.
 *   - Call `isAvatarLoaded(url)` at render time. If it returns `true`, the
 *     image is guaranteed to be in cache and the consuming component can
 *     skip its loading skeleton / opacity fade-in.
 *   - Call `markAvatarLoaded(url)` from an `<img onLoad>` handler so that
 *     subsequent renders of the same URL also skip the fade-in.
 */

const loadedSrcSet = new Set();
const inflightSet = new Set();

export const isAvatarLoaded = (url) => {
  if (!url) return false;
  return loadedSrcSet.has(url);
};

export const markAvatarLoaded = (url) => {
  if (!url) return;
  loadedSrcSet.add(url);
  inflightSet.delete(url);
};

export const preloadAvatar = (url) => {
  if (!url) return;
  if (loadedSrcSet.has(url) || inflightSet.has(url)) return;
  inflightSet.add(url);
  try {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => {
      loadedSrcSet.add(url);
      inflightSet.delete(url);
    };
    img.onerror = () => {
      inflightSet.delete(url);
    };
    img.src = url;
  } catch (e) {
    inflightSet.delete(url);
  }
};

export const preloadAvatars = (urls) => {
  if (!urls) return;
  urls.forEach((u) => preloadAvatar(u));
};
